/**
 * snapshotCache — a tiny, generic last-good cache over localStorage.
 *
 * Generalises the alt-data snapshot pattern: persist the last good payload so a
 * page reload during a transient source outage shows real (if stale) data
 * instantly instead of an empty view. Used by the intelligence map; the
 * alt-data page has its own (older) bespoke cache.
 *
 * Defensive: every storage touch is wrapped (private mode / quota / SSR /
 * corrupt JSON all degrade to a no-op or null rather than throwing).
 */

export interface Snapshot<T> {
  savedAt: number
  data: T
}

/** Persist `data` under `key` with a savedAt timestamp. No-op on failure. */
export function saveSnapshot<T>(key: string, data: T, now: number): void {
  try {
    const snap: Snapshot<T> = { savedAt: now, data }
    window.localStorage.setItem(key, JSON.stringify(snap))
  } catch {
    /* private mode, quota exceeded, or no window — ignore */
  }
}

/** Load the snapshot at `key`, or null if absent/corrupt/unavailable. */
export function loadSnapshot<T>(key: string): Snapshot<T> | null {
  try {
    const raw = window.localStorage.getItem(key)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Snapshot<T>
    if (!parsed || typeof parsed.savedAt !== 'number' || !('data' in parsed)) {
      return null
    }
    return parsed
  } catch {
    return null
  }
}

/**
 * True when a snapshot taken at `savedAt` is still within `maxAgeMs` of `now`.
 * Guards rehydration against showing a wildly stale (e.g. days-old) cache as if
 * it were live data. A future-dated savedAt (clock skew) counts as fresh.
 */
export function isSnapshotFresh(savedAt: number, now: number, maxAgeMs: number): boolean {
  return now - savedAt < maxAgeMs
}

/** Drop the snapshot at `key`. No-op on failure. */
export function clearSnapshot(key: string): void {
  try {
    window.localStorage.removeItem(key)
  } catch {
    /* ignore */
  }
}
